// Idle-lock state machine. Reads two timeouts from prefs (idleLockSeconds +
// forceLogoutSeconds); the two thresholds run in parallel from the last user
// activity in ANY tab of this browser (see ACTIVITY_KEY below). When the LOCK
// timer fires, set locked=true → the UI mounts the PIN
// overlay (see LockOverlay.jsx). When the LOGOUT timer fires (longer), call
// signOut for real — clears the server session, forces re-login. Activity events
// (mousemove / keydown / touchstart / mousedown) reset both timers, EXCEPT once
// the lock is engaged (we don't want bypassing by waving the mouse over the
// overlay).
//
// PIN gating: the LOCK timer only arms when the user actually has a PIN set
// (the overlay can't be dismissed without one — without this guard, clearing
// the PIN from Settings would trap the user behind an unverifiable overlay
// once the threshold fired). The LOGOUT timer is PIN-independent — it's just
// a session-expiry guard.
//
// ⚠ Honest scope: this is a casual deterrent (walk-away protection). Anyone
// with browser DevTools can dismiss the overlay locally. Real protection is the
// force-logout (server-side) AND the existing NINOCAT_SESSION_TTL.
import { createSignal, createEffect, onCleanup } from 'solid-js';
import { idleLockSeconds, forceLogoutSeconds } from './prefs.js';
import { pinEnabled } from './pin.js';
import { api } from './api.js';

// Lock state is PERSISTED (per-tab, sessionStorage) so an engaged lock survives a
// page reload — including a hard refresh (Ctrl+F5). Without this the in-memory
// signal reset to false on reload while the server session was still alive, so a
// passer-by could drop the walk-away overlay just by refreshing. sessionStorage
// (not localStorage) means it auto-clears when the tab closes and never leaks
// into another user's session on the same browser; authStore.login/logout also
// call forgetLock() to be sure.
const LOCK_KEY = 'ninocat.locked';
const readPersistedLock = () => {
  try { return sessionStorage.getItem(LOCK_KEY) === '1'; } catch { return false; }
};
const persistLock = (v) => {
  try { v ? sessionStorage.setItem(LOCK_KEY, '1') : sessionStorage.removeItem(LOCK_KEY); } catch (_) {}
};

const [locked, setLockedRaw] = createSignal(readPersistedLock());
export { locked };

// Persisting setter — every lock/unlock goes through here so the sessionStorage
// flag and the signal never diverge.
const setLocked = (v) => { persistLock(v); setLockedRaw(v); };

// Clear the lock (memory + persisted). Called on any sign-in / sign-out so a
// stale flag can't strand the next session behind an unverifiable overlay.
export function forgetLock() { setLocked(false); }

// Wired by Shell once: how to log the user out for real on the longer threshold.
let onForceLogout = null;
export function setOnForceLogout(fn) { onForceLogout = fn; }

// External trigger (e.g. a "Lock now" menu item) — engage the lock immediately.
// Only meaningful when a PIN is set; otherwise no-ops so we don't strand the user.
export function lockNow() { if (pinEnabled()) setLocked(true); }
// External: dismiss the lock (called by LockOverlay after a successful PIN).
export function unlock() { setLocked(false); /* timers re-arm on next activity */ }

// Activity is shared ACROSS TABS: they all ride one session cookie, so an idle
// background tab must not sign out (or lock) the tab being worked in. Each tab
// stamps its latest activity in localStorage (throttled), and a threshold timer
// that fires first re-checks the newest stamp from ANY tab — re-arming for the
// remainder instead of acting when some tab was active in the meantime.
const ACTIVITY_KEY = 'ninocat.lastActivity';
const STAMP_EVERY_MS = 2000;
const readSharedActivity = () => {
  try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; }
};
const writeSharedActivity = (t) => {
  try { localStorage.setItem(ACTIVITY_KEY, String(t)); } catch (_) {}
};

// Single watchdog: tracks lockSec + logoutSec, listens to activity, fires the
// right action at each threshold. Mount ONCE at app startup (Shell does this).
export function installIdleLock() {
  let lockT = null, logoutT = null;
  let lastActivity = Date.now(), lastStamp = 0;
  const clearTimers = () => { if (lockT) clearTimeout(lockT); if (logoutT) clearTimeout(logoutT); lockT = logoutT = null; };
  // ms left until `sec` of idleness in EVERY tab (≤ 0 → idle long enough).
  const idleLeft = (sec) => Math.max(lastActivity, readSharedActivity()) + sec * 1000 - Date.now();
  const armLock = (ms) => {
    lockT = setTimeout(() => {
      const left = idleLeft(idleLockSeconds());
      if (left > 0) armLock(left); else setLocked(true);
    }, ms);
  };
  const armLogout = (ms) => {
    logoutT = setTimeout(() => {
      const left = idleLeft(forceLogoutSeconds());
      if (left > 0) { armLogout(left); return; }
      // Force a real sign-out and the page reloads to /login. We try the
      // wired-in handler first (clean shutdown of in-flight state); if it's
      // missing we fall back to a raw API call + redirect.
      try { onForceLogout ? onForceLogout() : api.post('/api/auth/logout').finally(() => { location.href = '/login'; }); }
      catch (_) { location.href = '/login'; }
    }, ms);
  };
  const arm = () => {
    clearTimers();
    const lockSec = idleLockSeconds();
    const logoutSec = forceLogoutSeconds();
    // Lock overlay only arms when both the threshold AND a PIN exist. Without
    // a PIN, the overlay is unverifiable — better to skip it entirely than to
    // engage and trap the user (the 5-bad-tries safety valve would kick them
    // to /login, but that's a poor experience for a misconfiguration).
    if (lockSec > 0 && pinEnabled()) armLock(lockSec * 1000);
    if (logoutSec > 0) armLogout(logoutSec * 1000);
  };

  const onActivity = () => {
    if (locked()) return;     // overlay is up — don't reset (would defeat the lock)
    const now = Date.now();
    lastActivity = now;
    if (now - lastStamp >= STAMP_EVERY_MS) { lastStamp = now; writeSharedActivity(now); }
    // Nothing armed if: lock timer is meaningless (no threshold OR no PIN)
    // AND force-logout timer is off (no threshold).
    const lockArmed = idleLockSeconds() > 0 && pinEnabled();
    const logoutArmed = forceLogoutSeconds() > 0;
    if (!lockArmed && !logoutArmed) return;
    arm();
  };

  const events = ['mousemove', 'mousedown', 'keydown', 'touchstart'];
  for (const e of events) window.addEventListener(e, onActivity, { passive: true });
  onCleanup(() => { for (const e of events) window.removeEventListener(e, onActivity); clearTimers(); });

  // Re-arm whenever any tracked input changes — thresholds (Settings) OR PIN
  // existence (the user setting / clearing it in another tab updates the signal).
  createEffect(() => { idleLockSeconds(); forceLogoutSeconds(); pinEnabled(); arm(); });
}
